import React from 'react';
import { Eye, ChevronRight } from 'lucide-react';
import { StatusBadge } from '../shared/StatusBadge';
import { TimeRemaining } from '../shared/TimeRemaining';

interface RequestCardProps {
  request: any;
  onHide: (id: string) => void;
  onViewDetails: (request: any) => void;
  onSubmitBid: (id: string) => void;
  bidAmount: string;
  onBidAmountChange: (id: string, amount: string) => void;
  isSubmitting: boolean;
  error: string | null;
  isHidden: boolean;
}

export function RequestCard({
  request,
  onHide,
  onViewDetails,
  onSubmitBid,
  bidAmount,
  onBidAmountChange,
  isSubmitting,
  error,
  isHidden,
}: RequestCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center space-x-2 mb-2">
            <StatusBadge status={request.bid_status} />
          </div>
          <h3 className="text-lg font-medium text-gray-900">
            {request.car_make} {request.car_model}
          </h3>
          <p className="text-sm text-gray-500">Value: £{request.car_value?.toLocaleString('en-GB')}</p>
          <p className="text-sm text-gray-500 font-mono">{request.registration}</p>
          <p className="text-sm text-gray-500">Year: {request.car_year}</p>
        </div>
        <div className="text-xs text-gray-500">
          <p>Posted: {new Date(request.created_at).toLocaleDateString('en-GB')}</p>
          <p className="mt-1 text-orange-600">
            <TimeRemaining expiresAt={request.expires_at} />
          </p>
          <div className="mt-2">
            {request.lowest_bid !== null && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">
                  Current lowest bid: £{request.lowest_bid.toLocaleString('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>
                {request.has_user_bid && (
                  <p className={`text-xs font-medium ${
                    request.is_lowest_bidder ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    {request.is_lowest_bidder
                      ? 'Your bid is currently the lowest'
                      : 'Another insurer has a lower bid'}
                  </p>
                )}
              </div>
            )}
            {!request.has_user_bid && (
              <p className="text-xs font-medium text-gray-500">
                You have not placed a bid yet
              </p>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {request.bid_count} bid{request.bid_count !== 1 ? 's' : ''} from {request.unique_insurers} insurer{request.unique_insurers !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="border-t border-b py-4 my-4 space-y-3">
        <div className="flex items-center text-sm">
          <span className="text-gray-500 w-32">Customer Age:</span>
          <span>
            {request.age} years{' '}
            <span className="text-sm text-gray-500">
              ({new Date(request.date_of_birth).toLocaleDateString('en-GB', {
                month: 'long',
                year: 'numeric'
              })})
            </span>
          </span>
        </div>
        <div className="flex items-center text-sm">
          <span className="text-gray-500 w-32">Gender:</span>
          <span className="capitalize">{request.gender || 'Not specified'}</span>
        </div>
        <div className="flex items-center text-sm">
          <span className="text-gray-500 w-32">Postcode:</span>
          <span>{request.address?.split(',').pop()?.trim() || 'Not available'}</span>
        </div>
        <div className="flex items-center text-sm">
          <span className="text-gray-500 w-32">Mileage:</span>
          <span>{request.mileage.toLocaleString()} miles</span>
        </div>
        <div className="flex items-center text-sm">
          <span className="text-gray-500 w-32">No Claims:</span>
          <span>{request.no_claims_years} years</span>
        </div>
        <div className="flex items-center text-sm">
          <span className="text-gray-500 w-32">Coverage:</span>
          <span className="capitalize">{request.coverage_type.replace(/-/g, ' ')}</span>
        </div>
      </div>

      <div className="pt-4">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => onViewDetails(request)}
            className="group flex items-center space-x-1 hover:text-indigo-600 transition-colors"
          >
            <span>View Details</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={() => onHide(request.id)}
            className="text-gray-600 hover:text-gray-900 focus:outline-none flex items-center"
            title={isHidden ? "Unhide request" : "Hide request"}
          >
            <Eye className="w-5 h-5" />
            <span className="ml-2">{isHidden ? "Unhide Request" : "Hide Request"}</span>
          </button>
        </div>

        {new Date(request.expires_at) < new Date() ? (
          <div className="p-4 bg-gray-100 rounded-md text-gray-600 text-center">
            {request.lowest_bid ? (
              <>Final price: £{request.lowest_bid.toLocaleString('en-GB')}</>
            ) : (
              'Request expired with no bids'
            )}
          </div>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Bid</label>
            <div className="flex space-x-2">
              {error && (
                <div className="absolute -top-8 left-0 right-0 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                  {error}
                </div>
              )}
              <div className="relative flex-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                  £
                </span>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => onBidAmountChange(request.id, e.target.value)}
                  className="block w-full pl-7 pr-12 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <button
                onClick={() => onSubmitBid(request.id)}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex-shrink-0"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Bid'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}